// Passagem de bastão da fila de cobrança manual para a montagem da rota.
//
// A fila de `#/financeiro/lembretes-cobranca?aba=manuais` é por CLIENTE (um card
// por CPF/CNPJ e etapa); a parada da rota é a UC. Quem está trabalhando a fila
// quer marcar vários cards e dizer "faz uma rota com esses" — e cada card leva
// junto todas as UCs daquele cliente, que é o que se vai visitar.
//
// POR QUE sessionStorage E NÃO A URL
//
// Um card pode ter 4 UCs e a seleção chega fácil a 40 códigos de 11 dígitos.
// Isso na query string é uma URL de 500+ caracteres que o usuário vê, copia
// errado e cola quebrada. E a Central de Inadimplência não é uma rota própria:
// ela abre como overlay por um evento (`vigorhub:open-inadimplencia`), então não
// há URL onde pendurar nada. sessionStorage some ao fechar a aba, que é
// exatamente o tempo de vida desta intenção.

const CHAVE = 'vigorhub:rota-cobranca:preselecao';

export interface PreselecaoRota {
    /** Códigos de UC como aparecem na fatura. A tela casa pelos dois números da
     *  Equatorial, então não precisa vir normalizado. */
    ucs: string[];
    /** De onde veio, só para a mensagem na tela ("12 UCs vindas da fila D+24"). */
    origem: string;
    /** Descartada se ficar velha: seleção de ontem reaparecendo ao abrir a
     *  Central seria pior do que não ter seleção nenhuma. */
    em: number;
}

/** Meia hora. Tempo de ir da fila até a Central, com folga para uma interrupção. */
const VALIDADE_MS = 30 * 60 * 1000;

export const guardarPreselecao = (ucs: string[], origem: string): void => {
    try {
        const limpos = Array.from(new Set(ucs.map(u => String(u || '').trim()).filter(Boolean)));
        if (limpos.length === 0) return;
        sessionStorage.setItem(CHAVE, JSON.stringify({ ucs: limpos, origem, em: Date.now() }));
    } catch {
        // Aba anônima ou storage bloqueado: a Central abre sem seleção e a pessoa
        // marca à mão. Não vale derrubar a navegação por causa disto.
    }
};

/** O mínimo de uma demanda do Painel de Cobrança e Documentos para achar as UCs dela. */
export interface DemandaComUc {
    /** Ignorado de propósito — ver `ucsDasDemandas`. */
    uc_numero?: string | null;
    bruto?: unknown;
}

/**
 * As UCs de um conjunto de demandas do Painel de Cobrança e Documentos.
 *
 * Vêm SÓ das faturas da régua de contato humano (D+10/17/24). A demanda da régua é
 * por cliente e guarda em `bruto.faturas` todas as faturas dele, cada uma com o
 * código da UC na Equatorial — inclusive as outras unidades do mesmo cliente, que
 * são paradas diferentes da rota.
 *
 * `uc_numero` não serve nem de plano B: nas filas de documento ele pode ser o id
 * interno da unidade. Medido em 15/09/2026 — o distrato mostra "UC 11" e "UC 77",
 * a Central não achou nada e avisou "não estão nesta lista".
 */
export const ucsDasDemandas = (demandas: DemandaComUc[]): string[] => {
    const codigos = new Set<string>();
    demandas.forEach(d => {
        const faturas = (d.bruto as { faturas?: Array<{ uc_codigo?: string | null }> } | null | undefined)?.faturas;
        if (!Array.isArray(faturas)) return;
        faturas.forEach(f => {
            const codigo = String(f?.uc_codigo || '').trim();
            if (codigo) codigos.add(codigo);
        });
    });
    return Array.from(codigos);
};

/** Lê E CONSOME: a seleção vale uma vez só. Reabrir a Central depois não pode
 *  ressuscitar uma lista que a pessoa já usou ou já descartou. */
export const consumirPreselecao = (): PreselecaoRota | null => {
    try {
        const cru = sessionStorage.getItem(CHAVE);
        if (!cru) return null;
        sessionStorage.removeItem(CHAVE);
        const p = JSON.parse(cru) as PreselecaoRota;
        if (!Array.isArray(p?.ucs) || p.ucs.length === 0) return null;
        if (!Number.isFinite(p.em) || Date.now() - p.em > VALIDADE_MS) return null;
        return p;
    } catch {
        return null;
    }
};
